// @greenlight-spec: 10-security-compliance.md
// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 28-it-dashboard-ui.md

import { Navigate, Outlet } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

function FullPageSpinner() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <div className="h-8 w-8 motion-safe:animate-spin rounded-full border-b-2 border-primary" />
    </div>
  );
}

/**
 * Gates `/install` itself once the wizard has completed — the mirror image of
 * `InstallGuard`, which gates every *other* route until install finishes.
 * Without this, `/install` renders the editable wizard to anyone with the URL,
 * forever (docs/12 §Wizard-only window): `installedAt` never re-blanks, and
 * the wizard's own POST handlers refuse writes post-install, but the read-only
 * `GET /api/install/status` that drives this page stays public by design, so
 * the client must close the door itself.
 *
 * Resolves in order:
 *  1. No org row yet → render the wizard (`Outlet`); trivially not installed.
 *     Checked via `getContext` first (mirrors `InstallPage`) rather than
 *     calling `getStatus` directly — that endpoint 404s until an org exists,
 *     which the ts-rest query client treats as an error and retries before
 *     settling, stalling this gate behind a pointless retry backoff.
 *  2. Org exists + a *confirmed* "not installed" status → render the wizard.
 *  3. Org exists but the status errored or is unavailable → fail closed: treat
 *     it as installed and redirect, never render the wizard. We can't prove the
 *     org isn't installed, so we don't expose the editable wizard on doubt. The
 *     authoritative block is server-side — every mutating `/api/install/*` route
 *     409s once installed (docs/12 §Wizard-only window) — so a stale/errored
 *     read can never let a real write through; this only keeps the UI honest.
 *  4. Installed → redirect: an authenticated caller to the dashboard, otherwise
 *     to `/login`. Post-install the wizard is closed to everyone; ongoing
 *     reconfiguration lives in the Settings surfaces.
 */
export function InstallCompleteGuard() {
  const { user, loading: authLoading } = useAuth();
  const { data: contextData, isLoading: contextLoading } = api.install.getContext.useQuery(
    ['install-context'],
    {},
  );
  const orgExists = contextData?.status === 200 && contextData.body.orgExists;
  const { data: statusData, isLoading: statusLoading } = api.install.getStatus.useQuery(
    ['install-status'],
    {},
    { queryKey: ['install-status'], enabled: orgExists },
  );

  if (contextLoading || authLoading) {
    return <FullPageSpinner />;
  }

  if (!orgExists) {
    return <Outlet />;
  }

  if (statusLoading) {
    return <FullPageSpinner />;
  }

  // Fail closed: reopen the wizard only on a confirmed "not installed" read. A
  // missing or errored status body is treated as installed — anything short of
  // proof reopens nothing.
  const body = statusData?.status === 200 ? statusData.body : null;
  const confirmedNotInstalled = body !== null && body.installedAt === null;

  if (confirmedNotInstalled) {
    return <Outlet />;
  }

  return <Navigate to={user ? '/apps' : '/login'} replace />;
}
